// Manager for Wikipedia article fetching Web Worker
// Similar structure to iconWorkerManager

class WikiWorkerManager {
  constructor() {
    this.worker = null;
    this.pendingRequests = new Map();
    this.requestId = 0;
    this.isInitialized = false;
  }

  async initialize() {
    if (this.isInitialized) return;
    try {
      // Worker script is placed in public/ so it is served from root
      this.worker = new Worker('/wikiWorker.js');
      this.worker.onmessage = (e) => {
        const { id, type, result, error } = e.data;
        const pending = this.pendingRequests.get(id);
        if (!pending) return;
        this.pendingRequests.delete(id);
        if (type === 'articlesFetched') {
          pending.resolve(result);
        } else if (type === 'diffFetched') {
          pending.resolve(result);
        } else if (type === 'error') {
          pending.reject(new Error(error || 'Worker error'));
        }
      };
      this.worker.onerror = (err) => {
        console.error('[WikiWorker] Error:', err);
        this.pendingRequests.forEach((p) => p.reject(new Error('Worker crashed')));
        this.pendingRequests.clear();
      };
      this.isInitialized = true;
    } catch (err) {
      console.error('Failed to initialize wiki worker', err);
      throw err;
    }
  }

  async fetchArticles(bounds, wikiLanguage, thumbResolution = 180) {
    await this.initialize();
    return new Promise((resolve, reject) => {
      const id = ++this.requestId;
      this.pendingRequests.set(id, { resolve, reject });

      // Convert google.maps.LatLngBounds to plain numbers if needed
      const northEast = bounds.getNorthEast ? bounds.getNorthEast() : bounds.northEast;
      const southWest = bounds.getSouthWest ? bounds.getSouthWest() : bounds.southWest;

      const payloadBounds = {
        northEast: {
          lat: typeof northEast.lat === 'function' ? northEast.lat() : northEast.lat,
          lng: typeof northEast.lng === 'function' ? northEast.lng() : northEast.lng,
        },
        southWest: {
          lat: typeof southWest.lat === 'function' ? southWest.lat() : southWest.lat,
          lng: typeof southWest.lng === 'function' ? southWest.lng() : southWest.lng,
        },
      };

      this.worker.postMessage({
        id,
        type: 'fetchArticles',
        data: {
          bounds: payloadBounds,
          wikiLanguage,
          thumbResolution,
        },
      });

      // Timeout safety
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error('Wiki worker timeout'));
        }
      }, 15000);
    });
  }

  async fetchArticlesWithDiff(bounds, wikiLanguage, thumbResolution = 180, currentArticles) {
    await this.initialize();
    return new Promise((resolve, reject) => {
      const id = ++this.requestId;
      this.pendingRequests.set(id, { resolve, reject });

      // Convert google.maps.LatLngBounds to plain numbers if needed
      const northEast = bounds.getNorthEast ? bounds.getNorthEast() : bounds.northEast;
      const southWest = bounds.getSouthWest ? bounds.getSouthWest() : bounds.southWest;

      const payloadBounds = {
        northEast: {
          lat: typeof northEast.lat === 'function' ? northEast.lat() : northEast.lat,
          lng: typeof northEast.lng === 'function' ? northEast.lng() : northEast.lng,
        },
        southWest: {
          lat: typeof southWest.lat === 'function' ? southWest.lat() : southWest.lat,
          lng: typeof southWest.lng === 'function' ? southWest.lng() : southWest.lng,
        },
      };

      this.worker.postMessage({
        id,
        type: 'fetchWithDiff',
        data: {
          bounds: payloadBounds,
          wikiLanguage,
          thumbResolution,
          currentArticles,
        },
      });

      // Timeout safety
      setTimeout(() => {
        if (this.pendingRequests.has(id)) {
          this.pendingRequests.delete(id);
          reject(new Error('Wiki worker diff timeout'));
        }
      }, 15000);
    });
  }

  terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.isInitialized = false;
      this.pendingRequests.forEach((p) => p.reject(new Error('Worker terminated')));
      this.pendingRequests.clear();
    }
  }
}

export const wikiWorkerManager = new WikiWorkerManager();